import React from "react"
import { useToast } from "~/utils/Toast"
import getAppConfig from "~/utils/getAppConfig"

import { apiWorkspaceTasks } from "~/routes"
import CatchModal from "~/library/CatchModal"
import type Task from "~/types/Task"
import useFetchResource from "~/hooks/useFetchResource"
import LoadingWrapper from "~/library/LoadingWrapper"
import FormikWrapper from "~/library/FormikWrapper"
import FormikInput from "~/library/FormikInput"
import FormikSelect from "~/library/FormikSelect"
import FormikTextarea from "~/library/FormikTextarea"
import * as Yup from "yup"

const taskSchema = Yup.object({
    title: Yup.string().required("Title is required"),
    description: Yup.string(),
    taskType: Yup.string().required("Task type is required"),
    status: Yup.string().required("Status is required"),
})

interface EditTaskProps {
    isOpen: boolean
    onClose: () => void
    onSuccess?: () => void
    taskPrefixId: string
}

const EditTask: React.FC<EditTaskProps> = ({ isOpen, onClose, onSuccess, taskPrefixId }) => {
    const { venueSlug } = getAppConfig()
    const toast = useToast()

    const endpoint = apiWorkspaceTasks.show.path({ venue_slug: venueSlug, id: taskPrefixId })
    const { data: task, isLoading } = useFetchResource<{ task: Task }, "task">(
        endpoint,
        "task",
        {}
    )

    const handleSubmit = (formValues: any) => {
        if (!task) return

        const data = {
            venue_slug: venueSlug,
            id: taskPrefixId,
            task: {
                title: formValues.title,
                description: formValues.description,
                task_type: formValues.taskType,
                status: formValues.status,
            },
        }

        const req = apiWorkspaceTasks.update({ data })

        req
            .then(() => {
                toast("Task updated successfully", "success")
                onSuccess?.()
                onClose()
            })
            .catch((res) => {
                toast(
                    `Error updating task. ${res.body?.errors?.join(", ") || "Please try again"}`,
                    "danger"
                )
            })
    }

    const initialValues = {
        title: task?.title || "",
        description: task?.description || "",
        taskType: task?.taskType || "follow_up",
        status: task?.status || "not_started",
    }

    const taskTypeOptions = [
        { value: "call", label: "Call" },
        { value: "meeting", label: "Meeting" },
        { value: "follow_up", label: "Follow Up" },
        { value: "proposal", label: "Proposal" },
        { value: "renewal", label: "Renewal" },
    ]

    const statusOptions = [
        { value: "not_started", label: "Not Started" },
        { value: "in_progress", label: "In Progress" },
        { value: "completed", label: "Completed" },
        { value: "cancelled", label: "Cancelled" },
    ]

    return (
        <CatchModal isOpen={isOpen} onClose={onClose} title="Edit Task" size="lg">
            <LoadingWrapper isLoading={isLoading}>
                {task && (
                    <FormikWrapper
                        onSubmit={handleSubmit}
                        validationSchema={taskSchema}
                        initialValues={initialValues}
                    >
                        <FormikInput
                            name="title"
                            label="Title"
                            placeholder="Enter task title"
                            required
                            helpText=""
                        />

                        <FormikTextarea
                            name="description"
                            label="Description"
                            placeholder="Enter task description (optional)"
                            rows={8}
                            maxLength={1200}
                        />

                        {task.deal && (
                            <div className="mb-3">
                                <label className="form-label">Deal</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={task.deal.name}
                                    disabled
                                />
                                <small className="text-muted">Task relationships cannot be changed</small>
                            </div>
                        )}

                        {task.account && (
                            <div className="mb-3">
                                <label className="form-label">Account</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={task.account.name}
                                    disabled
                                />
                                <small className="text-muted">Task relationships cannot be changed</small>
                            </div>
                        )}

                        {task.contact && (
                            <div className="mb-3">
                                <label className="form-label">Contact</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={`${task.contact.firstName} ${task.contact.lastName}`}
                                    disabled
                                />
                                <small className="text-muted">Task relationships cannot be changed</small>
                            </div>
                        )}

                        <div className="row">
                            <div className="col-6">
                                <FormikSelect
                                    name="taskType"
                                    label="Task Type"
                                    options={taskTypeOptions}
                                    required
                                    helpText=""
                                />
                            </div>
                            <div className="col-6">
                                <FormikSelect
                                    name="status"
                                    label="Status"
                                    options={statusOptions}
                                    required
                                    helpText=""
                                />
                            </div>
                        </div>

                        <div className="d-flex justify-content-end gap-2 mt-3">
                            <button type="button" className="btn btn-secondary" onClick={onClose}>
                                Cancel
                            </button>
                            <button type="submit" className="btn btn-primary">
                                Update Task
                            </button>
                        </div>
                    </FormikWrapper>
                )}
            </LoadingWrapper>
        </CatchModal>
    )
}

export default EditTask
